import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from './ui/Modal';
import { Input } from './ui/Input';
import { Button } from './ui/Button';
import { useProjectStore } from '../store/projectStore';
import { useClientStore } from '../store/clientStore';
import { timeEntriesAPI, categoriesAPI, Category, TimeEntryCreate, TimeEntryCategory } from '../services/api';

// Get date string in local timezone
const getLocalDateString = (date: Date = new Date()): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

interface BulkEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialDate?: string;
}

export const BulkEntryModal: React.FC<BulkEntryModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialDate,
}) => {
  const { t } = useTranslation();
  const { projects } = useProjectStore();
  const { clients } = useClientStore();

  const [formData, setFormData] = useState({
    project_id: '',
    category_id: '',
    description: '',
    hours: '',
  });
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<{ created: number; errors: number } | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);

  // Fetch categories when project changes
  useEffect(() => {
    if (formData.project_id) {
      categoriesAPI.list(formData.project_id).then(setCategories).catch(() => setCategories([]));
    } else {
      setCategories([]);
    }
  }, [formData.project_id]);

  // Generate the current week's dates
  const generateWeekDates = () => {
    const today = new Date();
    const dates: { date: string; label: string; dayName: string }[] = [];

    // Start from Monday of current week
    const dayOfWeek = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));

    for (let i = 0; i < 7; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      const dateStr = getLocalDateString(date);
      dates.push({
        date: dateStr,
        label: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
      });
    }

    return dates;
  };

  const weekDates = generateWeekDates();

  useEffect(() => {
    if (isOpen) {
      // Reset form when opening
      setFormData({
        project_id: '',
        category_id: '',
        description: '',
        hours: '',
      });
      setSelectedDates(initialDate ? [initialDate] : []);
      setFormError('');
      setResult(null);
    }
  }, [isOpen, initialDate]);

  const handleToggleDate = (date: string) => {
    setSelectedDates((prev) =>
      prev.includes(date) ? prev.filter((d) => d !== date) : [...prev, date]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.project_id) {
      setFormError('Please select a project');
      return;
    }
    if (!formData.category_id) {
      setFormError('Please select a category');
      return;
    }
    if (!formData.description.trim()) {
      setFormError('Please enter a description');
      return;
    }
    const hours = parseFloat(formData.hours);
    if (isNaN(hours) || hours <= 0 || hours > 24) {
      setFormError('Hours must be between 0.01 and 24');
      return;
    }
    if (selectedDates.length === 0) {
      setFormError('Please select at least one date');
      return;
    }

    // Get category name for the entry
    const category = categories.find(c => c.id === formData.category_id);
    const categoryName = category?.name || 'Development';

    setIsSubmitting(true);
    setFormError('');

    try {
      const entries: TimeEntryCreate[] = selectedDates.map((date) => ({
        project_id: formData.project_id,
        date,
        description: formData.description.trim(),
        hours: parseFloat(hours.toFixed(2)),
        category: categoryName as TimeEntryCategory,
      }));

      const response = await timeEntriesAPI.createBulk(entries);

      setResult({
        created: response.total_created,
        errors: response.total_errors,
      });

      if (response.total_created > 0) {
        // Clear form on success
        setFormData({
          project_id: '',
          category_id: '',
          description: '',
          hours: '',
        });
        setSelectedDates([]);

        // Show success message briefly then close
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 1500);
      }

      if (response.total_errors > 0) {
        setFormError(
          `Some entries failed: ${response.errors.map((e) => e.error).join(', ')}`
        );
      }
    } catch (err: any) {
      setFormError(err.response?.data?.detail || 'Failed to create entries');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Group projects by client
  const groupedProjects = clients.map((client) => ({
    client,
    projects: projects.filter((p) => p.client_id === client.id && p.is_active),
  })).filter((group) => group.projects.length > 0);

  const activeCategories = categories.filter((c) => c.is_active);
  const todayStr = getLocalDateString();

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Multiple Entries" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Project
          </label>
          <select
            value={formData.project_id}
            onChange={(e) => setFormData({ ...formData, project_id: e.target.value, category_id: '' })}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            <option value="">Select a project...</option>
            {groupedProjects.map((group) => (
              <optgroup key={group.client.id} label={group.client.name}>
                {group.projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Category
          </label>
          <select
            value={formData.category_id}
            onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            <option value="">Select a category...</option>
            {activeCategories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="What did you work on?"
            rows={2}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>

        <Input
          label="Hours (per day)"
          type="number"
          step="0.01"
          min="0.01"
          max="24"
          value={formData.hours}
          onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
          placeholder="e.g., 2.0"
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Apply to dates (select multiple)
          </label>
          <div className="grid grid-cols-7 gap-2">
            {weekDates.map((d) => (
              <button
                key={d.date}
                type="button"
                onClick={() => handleToggleDate(d.date)}
                className={`p-2 rounded-lg border text-center transition-colors ${
                  selectedDates.includes(d.date)
                    ? 'bg-green-600 border-green-600 text-white'
                    : d.date === todayStr
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300'
                    : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                <div className="text-xs font-medium">{d.dayName}</div>
                <div className="text-sm">{d.label.split(' ')[1]}</div>
              </button>
            ))}
          </div>
          {selectedDates.length > 0 && (
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {selectedDates.length} date{selectedDates.length !== 1 ? 's' : ''} selected
            </p>
          )}
        </div>

        {formError && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <p className="text-sm text-red-600 dark:text-red-400">{formError}</p>
          </div>
        )}

        {result && result.created > 0 && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
            <p className="text-sm text-green-600 dark:text-green-400">
              Successfully created {result.created} entr{result.created !== 1 ? 'ies' : 'y'}!
            </p>
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-4">
          <Button variant="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button type="submit" isLoading={isSubmitting} disabled={selectedDates.length === 0}>
            Create {selectedDates.length > 0 ? `${selectedDates.length} Entr${selectedDates.length !== 1 ? 'ies' : 'y'}` : 'Entries'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
